import { createMachine, assign } from 'xstate';

import {
  getClientSideApiClient,
  isAxiosError,
  isAPIError,
} from '../../../openapi/axiosClient';
import { fetchCsrfToken } from '../../../infrastructure/api';
import {
  convertPaymentMethodForSubmission,
  isStripeSpecificError,
} from '../../../utils/payment';

import {
  ReservationFlowProductDetail,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
  ReservationFlowCustomerSubscriptionDetail,
  ReservationFlowTicketBookDetail,
  ReservationFlowInvalidRequestError,
  ReservationFlowResourceSchemeType,
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
} from '../../../interfaces/reservation_flow';
import { validateCustomerInfo } from '../functions/validate-customer-info';
import { isReservationFlowInvalidRequestError } from '../functions/reservation-flow-invalid-request/is-reservation-flow-invalid-request-error';
import {
  courseTypeProductPurchaseEvent,
  eventTypeProductPurchaseEvent,
  purchaseEvent,
} from '../../../utils/gtag';
import { StripeElements, Stripe } from '@stripe/stripe-js';
import { captureException } from '@sentry/nextjs';
import { MulpayCardData } from 'src/interfaces/mullpayCardData';
import { waitForPostMessage } from 'src/utils/wait-for-postmessage';
import { executeWithRetryForSpecificError } from 'src/utils/booking';
export interface ProductPurchaseContext {
  data: {
    merchantPublicId?: string;
    productPublicId?: number;
    productDetail?: ReservationFlowProductDetail;
    // パスワードで保護されたページで認証が必要かどうかを判定するフラグ
    isRequireVerifying?: true;
    isJCBEnabled?: boolean;
    stripeError?: stripe.StripeError;
    customerSubscriptionSubmitResult?: ReservationFlowCustomerSubscriptionDetail;
    ticketBookSubmitResult?: ReservationFlowTicketBookDetail;
    submitError?: ReservationFlowInvalidRequestError;
    resourceSchemeType?: ReservationFlowResourceSchemeType;
    stripeElements?: StripeElements;
    stripeV3Instance?: Stripe;
    stripeConfirmationTokenId?: string;
    // Mulpay:
    isMulpayOrderEnabled?: boolean;
    mulpayCardData?: MulpayCardData;
    mulpayPopupWindow?: Window | null;
  };
  input: {
    customerInfo?: { id: number; value: string }[];
    paymentMethod?:
      | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
      | ReservationFlowSelectedPaymentMethodNewCreditCard;
  };
}

export type ProductPurchaseEvent =
  | {
      type: 'SET_MERCHANT_PUBLIC_ID';
      payload: {
        merchantPublicId: string;
      };
    }
  | {
      type: 'SET_PRODUCT_PUBLIC_ID';
      payload: {
        productPublicId: number;
      };
    }
  | {
      type: 'SET_PRODUCT_DETAIL';
      payload: {
        productDetail: ReservationFlowProductDetail;
      };
    }
  | {
      type: 'SET_IS_REQUIRE_VERIFYING';
    }
  | {
      type: 'SET_IS_JCB_ENABLED';
      payload: {
        isJCBEnabled: boolean;
      };
    }
  | {
      type: 'SET_RESOURCE_SCHEME_TYPE';
      payload: {
        resourceSchemeType: ReservationFlowResourceSchemeType;
      };
    }
  | {
      type: 'GOTO_INITIALIZING';
    }
  | {
      type: 'INPUT_CUSTOMER_INFO';
      payload: {
        customerInfo: { id: number; value: string }[];
      };
    }
  | {
      type: 'INPUT_PAYMENT_METHOD';
      payload: {
        paymentMethod:
          | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
          | ReservationFlowSelectedPaymentMethodNewCreditCard;
      };
    }
  | {
      type: 'CONFIRM';
    }
  | {
      type: 'SUBSCRIPTION_SUBMIT';
    }
  | {
      type: 'TICKET_BOOK_SUBMIT';
    }
  | {
      type: 'BACK_FROM_CONFIRMING_TO_MAIN';
    }
  | {
      type: 'RESET_DATA';
    }
  | {
      type: 'RESET_INPUT';
    }
  | {
      type: 'done.invoke.subscriptionSubmitting';
      data: ReservationFlowCustomerSubscriptionDetail;
    }
  | {
      type: 'done.invoke.ticketBookSubmitting';
      data: ReservationFlowTicketBookDetail;
    }
  | {
      type: 'SET_STRIPE_PAYMENT_ELEMENTS';
      payload: {
        stripeElements: StripeElements;
      };
    }
  | {
      type: 'RESET_STRIPE_PAYMENT_ELEMENTS';
    }
  | {
      type: 'SET_STRIPE_INSTANCE';
      payload: {
        stripeV3Instance: Stripe;
      };
    }
  | {
      type: 'SET_MULPAY_MP_TOKEN';
      payload: {
        mulpayMpToken: string;
      };
    }
  | {
      type: 'SET_MULPAY_CARD_DATA';
      payload: {
        mulpayCardData: MulpayCardData;
      };
    }
  | {
      type: 'RESET_MULPAY_INPUT';
    }
  | {
      type: 'SET_IS_MULPAY_ORDER_ENABLED';
      payload: {
        isMulpayOrderEnabled: boolean;
      };
    }
  | {
      type: 'SET_MULPAY_POPUP_WINDOW';
      payload: {
        popupWindow: Window | null;
      };
    };

export const canTransitFromInitializingToWaitingForVerifying: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  if (typeof context.data.productPublicId === 'undefined') {
    return false;
  }

  // productDetailが取得できている場合はすでに認証が完了しているか認証が不要
  if (typeof context.data.productDetail !== 'undefined') {
    return false;
  }

  // APIの結果取得まで判定を遅延させる
  if (typeof context.data.isRequireVerifying === 'undefined') {
    return false;
  }

  return context.data.isRequireVerifying;
};

export const canTransitFromInitializingToMain: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  if (
    typeof context.data.merchantPublicId === 'undefined' ||
    typeof context.data.productPublicId === 'undefined' ||
    typeof context.data.productDetail === 'undefined'
  ) {
    return false;
  }

  return true;
};

// FIXME: yupのバリデーションスキーマに移行する
export const isValidInputCustomerInfo: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  const dataCustomerFields = context.data.productDetail?.customer_fields;
  const inputCustomerInfo = context.input.customerInfo;

  if (typeof dataCustomerFields === 'undefined') {
    return false;
  }

  return dataCustomerFields.every((field) => {
    const inputInfo = inputCustomerInfo?.find((info) => info.id === field.id);

    if (field.is_required && typeof inputInfo === 'undefined') {
      return false;
    }

    const { isValid } = validateCustomerInfo(field, inputInfo?.value);

    return isValid;
  });
};

// FIXME: yupのバリデーションスキーマに移行する
export const isValidInputPaymentMethodNewCreditCard: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  return isValidStripeV3InputPaymentMethodNewCreditCard(context);
};

const isValidInputPaymentMethodNewMulpayCreditCard: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  const inputPaymentMethod = context.input.paymentMethod;

  if (!context.data.isMulpayOrderEnabled) {
    return false;
  }

  if (typeof inputPaymentMethod === 'undefined') {
    return false;
  }

  if (inputPaymentMethod.type !== 'new_credit_card') {
    return false;
  }

  return (
    context.data.mulpayCardData?.type === 'new' &&
    context.data.mulpayCardData.mulpayMpToken !== undefined
  );
};

const isValidStripeV3InputPaymentMethodNewCreditCard: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  const inputPaymentMethod = context.input.paymentMethod;

  if (typeof inputPaymentMethod === 'undefined') {
    return false;
  }

  if (inputPaymentMethod.type !== 'new_credit_card') {
    return false;
  }

  if (context.data.stripeElements === undefined) {
    return false;
  }

  return true;
};

// FIXME: yupのバリデーションスキーマに移行する
// GMOもStripeも既存カードの場合はtokenがないためこちらで判定される
export const isValidInputPaymentMethod: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  const inputPaymentMethod = context.input.paymentMethod;

  if (typeof inputPaymentMethod === 'undefined') {
    return false;
  }

  if (inputPaymentMethod.type !== 'credit_card') {
    return false;
  }

  if (!inputPaymentMethod.is_restore_credit_card) {
    return false;
  }

  return true;
};

export const canTransitFromMainToCompleteWithMulpayCreditCardToken: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  return (
    isValidInputCustomerInfo(context) &&
    isValidInputPaymentMethodNewMulpayCreditCard(context)
  );
};

export const canTransitFromMainToGettingStripeCreditCardTokenOnMain: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  if (
    !isValidInputCustomerInfo(context) ||
    !isValidInputPaymentMethodNewCreditCard(context)
  ) {
    return false;
  }

  return true;
};

export const canTransitFromMainToCompleteOnMain: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  if (
    !isValidInputCustomerInfo(context) ||
    !isValidInputPaymentMethod(context)
  ) {
    return false;
  }

  return true;
};

export const getStripeCreditCardToken: (
  context: ProductPurchaseContext,
) => Promise<string> = async (context) => {
  return getStripeConfirmationTokenId(context);
};

const getStripeConfirmationTokenId: (
  context: ProductPurchaseContext,
) => Promise<string> = async (context) => {
  const stripeElements = context.data.stripeElements!;
  const stripe = context.data.stripeV3Instance;

  if (context.input.paymentMethod?.type !== 'new_credit_card') {
    captureException('Never');
    throw new Error('Never');
  }
  if (!stripe) throw new Error('Failed to load stripe');

  const { confirmationToken, error } = await stripe.createConfirmationToken({
    elements: stripeElements,
    params: {
      payment_method_data: {
        billing_details: {
          address: {
            country: 'JP',
          },
        },
      },
    },
  });
  if (error) {
    throw error;
  }
  return confirmationToken.id;
};

export const canTransitFromMainToSubscriptionSubmittingOnConfirming: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  const dataProductDetail = context.data.productDetail;
  if (dataProductDetail?.scheme !== 'subscription') {
    return false;
  }

  // GmoPgの場合は、popUpが指定されていない場合は決済に遷移させない
  if (context.data.isMulpayOrderEnabled) {
    // 翌月決済で既存カードの場合、popUpを利用しないため、常にtrueになる
    if (
      dataProductDetail.is_wait_first_anchor &&
      context.input.paymentMethod?.type === 'credit_card'
    ) {
      return true;
    } else {
      return !!context.data.mulpayPopupWindow;
    }
  }

  return true;
};

export const canTransitFromMainToTicketBookSubmittingOnConfirming: (
  context: ProductPurchaseContext,
) => boolean = (context) => {
  const dataProductDetail = context.data.productDetail;
  if (dataProductDetail?.scheme !== 'ticket_book') {
    return false;
  }

  // GmoPgの場合は、popUpが指定されていない場合は決済に遷移させない
  if (context.data.isMulpayOrderEnabled) {
    return !!context.data.mulpayPopupWindow;
  }

  return true;
};

// 翌月1日決済の場合に、GmoPg・Stripeのそれぞれに合わせて決済方法を登録する
const submitPaymentMethodForCreate = async (
  context: ProductPurchaseContext,
  csrfToken: string,
) => {
  let seti_code: string | undefined;
  let mulpay_token: string | undefined;
  const client = getClientSideApiClient();

  if (
    context.data.isMulpayOrderEnabled &&
    context.data.mulpayCardData?.type === 'new'
  ) {
    mulpay_token = context.data.mulpayCardData.mulpayMpToken;
  } else {
    const { client_secret: stripe_client_secret } =
      await client.setupPaymentMethod({
        parameter: {
          'X-Csrf-Token': csrfToken,
        },
      });

    if (!stripe_client_secret) {
      throw new Error('stripe_client_secret is not found');
    }

    const stripe = context.data.stripeV3Instance!;
    const { setupIntent, error } = await stripe.confirmSetup({
      clientSecret: stripe_client_secret,
      confirmParams: {
        confirmation_token: context.data.stripeConfirmationTokenId,
      },
      redirect: 'if_required',
    });
    if (error) throw error;

    seti_code = setupIntent.id;
  }

  return await client.createPaymentMethod({
    parameter: {
      'X-Csrf-Token': csrfToken,
    },
    requestBody: {
      seti_code,
      mulpay_token,
      client_type: 'merchant_web' as const,
    },
  });
};

// 予約フロー内でのモーダルでの1日決済(購入時点で決済が発生しない、クレジットの登録のみ)
const submitSubscriptionWaitFirstAnchor: (
  context: ProductPurchaseContext,
) => Promise<ReservationFlowCustomerSubscriptionDetail> = async (context) => {
  const dataMerchantPublicId = context.data.merchantPublicId!;
  const dataProductPublicId = context.data.productPublicId!;
  const input = context.input!;

  const client = getClientSideApiClient();

  const csrfToken = await fetchCsrfToken();

  // 新規のクレジットカードの場合は支払い方法の登録を実施する
  const response =
    input.paymentMethod?.type === 'new_credit_card'
      ? await submitPaymentMethodForCreate(context, csrfToken)
      : undefined;

  // GmoPgの3DS認証が必要な場合はpopupで3DS認証を実施して、完了まで待つ
  // クレジットカードによっては、GmoPgの場合でも3DS認証が発生せずに登録されるものがある
  const popup = context.data.mulpayPopupWindow;
  if (
    popup &&
    !popup.closed &&
    context.data.isMulpayOrderEnabled &&
    response?.gmo_pg_redirect_url &&
    response?.gmo_pg_access_id
  ) {
    // 以下から、 pupup で GMO 決済が進行する
    //   1. pupup で GMO へリダイレクトし、GMO で決済処理が進む
    //   2. pupup でそのまま　 GMO からのリダイレクトを受け取る
    //   3. pupup が 親ウインドウにメッセージを送信する
    // 1-3 までの処理完了を　waitForPostMessage　で待ち、 await promise で結果のメッセージを受け取る
    popup.location.href = response.gmo_pg_redirect_url;

    // popup が GMO-PG からのリダイレクトを受け取り、メッセージを送信するまで
    await waitForPostMessage<{ type: 'payment:gmo_pg_returned' }>({
      sourceWindow: popup,
      expectedType: 'payment:gmo_pg_returned',
      expectedOrigin: window.location.origin,
    });

    await client.confirmPaymentMethod({
      parameter: {
        'X-Csrf-Token': csrfToken,
      },
      requestBody: {
        gmo_pg_access_id: response.gmo_pg_access_id,
      },
    });
  }

  // 決済は行わず、クレカも更新が完了している前提になっている
  // FIXME: サブスクを作成するだけなのでpaymentMethodのcolumn自体廃止したい
  const paymentMethod = {
    type: 'stripe_payment_method',
    stripe_credit_card_token: null, // FIXME: 以下は不要だがスキーマで required のため指定している。3DS対応を終えたらスキーマからも消して綺麗にできる
    is_restore_credit_card: false,
  } as const;
  const result = await client.purchaseReservationFlowSubscription({
    parameter: {
      merchant_public_id: dataMerchantPublicId,
      'X-Csrf-Token': csrfToken,
    },
    requestBody: {
      product_public_id: dataProductPublicId,
      payment_method: paymentMethod,
      customer_fields:
        typeof input.customerInfo !== 'undefined' ? input.customerInfo : [],
    },
  });

  return result;
};

const submitProductWithMulPay = async (
  gmo_pg_redirect_url: string | null,
  context: ProductPurchaseContext,
) => {
  // `gmo_pg_redirect_url`は　GMO 決済の場合は必ず値が入っているが、
  // 型定義上 null が入りうるため、チェックを行う
  if (!gmo_pg_redirect_url) {
    throw new Error('Unexpected error');
  }

  const popup = context.data.mulpayPopupWindow;
  if (popup && !popup.closed) {
    // 以下から、 pupup で GMO 決済が進行する
    //   1. pupup で GMO へリダイレクトし、GMO で決済処理が進む
    //   2. pupup でそのまま　 GMO からのリダイレクトを受け取る
    //   3. pupup が 親ウインドウにメッセージを送信する
    // 1-3 までの処理完了を　waitForPostMessage　で待ち、 await promise で結果のメッセージを受け取る
    popup.location.href = gmo_pg_redirect_url;

    // popup が GMO-PG からのリダイレクトを受け取り、メッセージを送信するまで
    await waitForPostMessage<{ type: 'payment:gmo_pg_returned' }>({
      sourceWindow: popup,
      expectedType: 'payment:gmo_pg_returned',
      expectedOrigin: window.location.origin,
    });
  } else {
    // ユーザー操作でポップアップを出しているため、ユーザー設定でポップアップを許可していなくてもブロックの対象にはならないはず
    // popup が開けない状況は想定していないため、unexpected error として扱う
    throw new Error('Unexpected error');
  }
};

const submitProductWithStripe = async (
  stripe_client_secret: string,
  confirmationToken: string | null,
  context: ProductPurchaseContext,
) => {
  if (!stripe_client_secret) {
    // `stripe_client_secret`は型定義上nullが入りうるが、
    // paymentMethod.typeがgmoではないときは必ず値が入っている
    throw new Error('Unexpected error');
  }

  const stripe = context.data.stripeV3Instance!;
  const { error } = await stripe.confirmPayment({
    clientSecret: stripe_client_secret,
    confirmParams: {
      confirmation_token: confirmationToken ?? undefined,
    },
    redirect: 'if_required',
  });
  if (error) {
    if (isStripeSpecificError(error)) {
      throw error;
    }

    // NOTE: api call失敗と同様のエラー形式に変換してthrowする
    // FIXME: エラー形式を揃えることでお茶を濁しているがReservationFlowInvalidRequestErrorに依存しないエラーハンドリングにすべき
    // 対応範囲が広いので未対応
    throw wrapInvalidRequestError(
      error.message || 'FALLBACK_UNRECOVERABLE_ERROR',
    );
  }
};

// 予約フロー内でのモーダルでの日割り決済(購入時点で決済が発生する)
const submitSubscriptionWithPayment = async (
  context: ProductPurchaseContext,
) => {
  const dataMerchantPublicId = context.data.merchantPublicId!;
  const dataProductPublicId = context.data.productPublicId!;
  const confirmationTokenId = context.data.stripeConfirmationTokenId;
  const mulpayCardData = context.data.mulpayCardData;
  const input = context.input!;

  const paymentMethod = convertPaymentMethodForSubmission(
    input.paymentMethod!,
    {
      stripeConfirmationTokenId: confirmationTokenId,
      productType: 'subscription',
      mulpayCardData,
      isMulpayOrderEnabled: context.data.isMulpayOrderEnabled,
    },
  );

  const client = getClientSideApiClient();

  const csrfToken = await fetchCsrfToken();

  const response = await client.startReservationFlowSubscriptionPurchase({
    parameter: {
      merchant_public_id: dataMerchantPublicId,
      'X-Csrf-Token': csrfToken,
    },
    requestBody: {
      product_public_id: dataProductPublicId,
      payment_method: paymentMethod,
      customer_fields:
        typeof input.customerInfo !== 'undefined' ? input.customerInfo : [],
    },
  });

  if (
    paymentMethod.type === 'prepayment_with_gmo_credit' ||
    paymentMethod.type === 'onfile_prepayment_with_gmo_credit'
  ) {
    await submitProductWithMulPay(response.gmo_pg_redirect_url, context);
  } else {
    await submitProductWithStripe(
      response.stripe_client_secret,
      paymentMethod.confirmation_token,
      context,
    );
  }

  // notifyConfirmed でカードを保存するケースは、GMO-PG決済の場合のみ(登録済みカードでの決済は除く)
  // Stripe決済では、この前の処理でカード保存がされているため、save_as_last_used は false とする
  const saveAsLastUsed =
    paymentMethod.type === 'prepayment_with_gmo_credit'
      ? !!paymentMethod.save_as_last_used
      : false;

  return await executeWithRetryForSpecificError(async () => {
    return await client.notifyConfirmedReservationFlowSubscription({
      parameter: {
        merchant_public_id: dataMerchantPublicId,
        'X-Csrf-Token': csrfToken,
      },
      requestBody: {
        product_issuance_id: response.product_issuance_id,
        save_as_last_used: saveAsLastUsed,
      },
    });
  });
};

export const submitSubscription: (
  context: ProductPurchaseContext,
) => Promise<ReservationFlowCustomerSubscriptionDetail> = async (context) => {
  const dataProductDetail = context.data.productDetail!;

  try {
    if (dataProductDetail.is_wait_first_anchor)
      return submitSubscriptionWaitFirstAnchor(context);
    else {
      return submitSubscriptionWithPayment(context);
    }
  } catch (error) {
    if (isAxiosError(error) && typeof error.response !== 'undefined') {
      if (
        isAPIError(error.response.data) &&
        isReservationFlowInvalidRequestError(error.response.data)
      ) {
        throw error.response.data;
      }
    }

    if (isStripeSpecificError(error)) {
      throw error;
    }

    // unrecoverable_errorとしてハンドリングする
    const fallbackUnrecoverableError: ReservationFlowInvalidRequestError = {
      code: 'internal_server_error',
      message: 'internal_server_error',
      details: [
        {
          type: 'reservation_flow_invalid_request',
          invalid_items: [
            {
              type: 'unrecoverable_error',
              message: 'FALLBACK_UNRECOVERABLE_ERROR',
            },
          ],
        },
      ],
      error: {
        type: 'internal_server_error',
      },
    };

    throw fallbackUnrecoverableError;
  }
};

const submitTicketBookWithPayment = async (context: ProductPurchaseContext) => {
  const dataMerchantPublicId = context.data.merchantPublicId!;
  const dataProductPublicId = context.data.productPublicId!;
  const confirmationTokenId = context.data.stripeConfirmationTokenId;
  const mulpayCardData = context.data.mulpayCardData;
  const input = context.input!;

  const paymentMethod = convertPaymentMethodForSubmission(
    input.paymentMethod!,
    {
      stripeConfirmationTokenId: confirmationTokenId,
      productType: 'ticket_book',
      mulpayCardData,
      isMulpayOrderEnabled: context.data.isMulpayOrderEnabled,
    },
  );

  const client = getClientSideApiClient();

  const csrfToken = await fetchCsrfToken();

  const response = await client.startTicketBookPurchase({
    parameter: {
      merchant_public_id: dataMerchantPublicId,
      'X-Csrf-Token': csrfToken,
    },
    requestBody: {
      product_public_id: dataProductPublicId,
      payment_method: paymentMethod,
      customer_fields:
        typeof input.customerInfo !== 'undefined' ? input.customerInfo : [],
    },
  });

  if (
    paymentMethod.type === 'prepayment_with_gmo_credit' ||
    paymentMethod.type === 'onfile_prepayment_with_gmo_credit'
  ) {
    await submitProductWithMulPay(response.gmo_pg_redirect_url, context);
  } else {
    await submitProductWithStripe(
      response.stripe_client_secret,
      paymentMethod.confirmation_token,
      context,
    );
  }

  // notifyConfirmed でカードを保存するケースは、GMO-PG決済の場合のみ(登録済みカードでの決済は除く)
  // Stripe決済では、この前の処理でカード保存がされているため、save_as_last_used は false とする
  const saveAsLastUsed =
    paymentMethod.type === 'prepayment_with_gmo_credit'
      ? !!paymentMethod.save_as_last_used
      : false;

  return await executeWithRetryForSpecificError(async () => {
    return await client.notifyConfirmedTicketBookPurchase({
      parameter: {
        merchant_public_id: dataMerchantPublicId,
        'X-Csrf-Token': csrfToken,
      },
      requestBody: {
        product_issuance_id: response.product_issuance_id,
        save_as_last_used: saveAsLastUsed,
      },
    });
  });
};

export const submitTicketBook: (
  context: ProductPurchaseContext,
) => Promise<ReservationFlowTicketBookDetail> = async (context) => {
  try {
    return await submitTicketBookWithPayment(context);
  } catch (error) {
    if (isAxiosError(error) && typeof error.response !== 'undefined') {
      if (
        isAPIError(error.response.data) &&
        isReservationFlowInvalidRequestError(error.response.data)
      ) {
        throw error.response.data;
      }
    }

    if (isStripeSpecificError(error)) {
      throw error;
    }

    // unrecoverable_errorとしてハンドリングする
    const fallbackUnrecoverableError: ReservationFlowInvalidRequestError = {
      code: 'internal_server_error',
      message: 'internal_server_error',
      details: [
        {
          type: 'reservation_flow_invalid_request',
          invalid_items: [
            {
              type: 'unrecoverable_error',
              message: 'FALLBACK_UNRECOVERABLE_ERROR',
            },
          ],
        },
      ],
      error: {
        type: 'internal_server_error',
      },
    };

    throw fallbackUnrecoverableError;
  }
};

const wrapInvalidRequestError: (
  message: string,
) => ReservationFlowInvalidRequestError = (message) => {
  return {
    code: 'internal_server_error',
    message: 'internal_server_error',
    details: [
      {
        type: 'reservation_flow_invalid_request',
        invalid_items: [
          {
            type: 'unrecoverable_error',
            message,
          },
        ],
      },
    ],
    error: {
      type: 'internal_server_error',
    },
  };
};

export const productPurchaseMachine = createMachine<
  ProductPurchaseContext,
  ProductPurchaseEvent
>(
  {
    predictableActionArguments: true,
    id: 'productPurchaseMachine',
    context: {
      data: {},
      input: {},
    },
    initial: 'initializing',
    states: {
      initializing: {
        always: [
          {
            target: 'waitingForVerifying',
            cond: 'canTransitFromInitializingToWaitingForVerifying',
          },
          {
            target: 'main',
            cond: 'canTransitFromInitializingToMain',
          },
        ],
      },
      waitingForVerifying: {
        on: {
          GOTO_INITIALIZING: {
            target: 'initializing',
          },
        },
      },
      main: {
        initial: 'main',
        onDone: {
          target: 'confirming',
        },
        states: {
          main: {
            on: {
              INPUT_CUSTOMER_INFO: {
                actions: ['assignInputCustomerInfo'],
              },
              INPUT_PAYMENT_METHOD: {
                actions: ['assignInputPaymentMethod'],
              },
              CONFIRM: [
                {
                  target: 'complete',
                  cond: 'canTransitFromMainToCompleteWithMulpayCreditCardToken',
                },
                {
                  target: 'gettingStripeCreditCardToken',
                  cond: 'canTransitFromMainToGettingStripeCreditCardTokenOnMain',
                },
                {
                  target: 'complete',
                  cond: 'canTransitFromMainToCompleteOnMain',
                },
              ],
              SET_STRIPE_PAYMENT_ELEMENTS: {
                actions: ['assignDataStripePaymentElements'],
              },
              RESET_STRIPE_PAYMENT_ELEMENTS: {
                actions: ['resetDataStripePaymentElements'],
              },
              SET_STRIPE_INSTANCE: {
                actions: ['assignDataStripeInstance'],
              },
              SET_MULPAY_MP_TOKEN: {
                actions: ['assignDataMulPayToken'],
              },
              RESET_MULPAY_INPUT: {
                actions: ['resetDataMulPayInput'],
              },
              SET_MULPAY_CARD_DATA: {
                actions: ['assignDataMulPayCardData'],
              },
            },
          },
          gettingStripeCreditCardToken: {
            invoke: {
              src: 'getStripeCreditCardToken',
              onDone: {
                target: 'complete',
                actions: [
                  'assignDataStripeCreditCardTokenForGetStripeCreditCardToken',
                ],
              },
              onError: {
                target: 'main',
                actions: ['assignDataStripeErrorForGetStripeCreditCardToken'],
              },
            },
          },
          complete: {
            type: 'final',
          },
        },
      },
      confirming: {
        initial: 'main',
        onDone: {
          target: 'complete',
        },
        states: {
          main: {
            on: {
              SUBSCRIPTION_SUBMIT: {
                target: 'subscriptionSubmitting',
                cond: 'canTransitFromMainToSubscriptionSubmittingOnConfirming',
              },
              TICKET_BOOK_SUBMIT: {
                target: 'ticketBookSubmitting',
                cond: 'canTransitFromMainToTicketBookSubmittingOnConfirming',
              },
              BACK_FROM_CONFIRMING_TO_MAIN: {
                target: '#productPurchaseMachine.main',
              },
            },
          },
          subscriptionSubmitting: {
            invoke: {
              id: 'subscriptionSubmitting',
              src: 'submitSubscription',
              onDone: {
                target: 'complete',
                actions: ['assignSubscriptionSubmitResultForSubmit'],
              },
              onError: {
                target: '#productPurchaseMachine.error',
                actions: ['assignSubmitErrorForSubmit'],
              },
            },
          },
          ticketBookSubmitting: {
            invoke: {
              id: 'ticketBookSubmitting',
              src: 'submitTicketBook',
              onDone: {
                target: 'complete',
                actions: ['assignTicketBookSubmitResultForSubmit'],
              },
              onError: {
                target: '#productPurchaseMachine.error',
                actions: ['assignSubmitErrorForSubmit'],
              },
            },
          },
          complete: {
            type: 'final',
          },
        },
      },
      complete: {},
      error: {},
    },
    on: {
      SET_MULPAY_POPUP_WINDOW: {
        actions: ['assignMulpayPopupWindow'],
      },
      SET_MERCHANT_PUBLIC_ID: {
        actions: ['assignDataMerchantPublicId'],
      },
      SET_PRODUCT_PUBLIC_ID: {
        actions: ['assignDataProductPublicId'],
      },
      SET_PRODUCT_DETAIL: {
        actions: ['assignDataProductDetail'],
      },
      SET_IS_REQUIRE_VERIFYING: {
        actions: ['assignDataIsRequireVerifying'],
      },
      SET_IS_JCB_ENABLED: {
        actions: ['assignDataIsJCBEnabled'],
      },
      SET_RESOURCE_SCHEME_TYPE: {
        actions: ['assignDataResourceSchemeType'],
      },
      RESET_DATA: {
        actions: ['unassignData'],
      },
      RESET_INPUT: {
        actions: ['unassignInput'],
      },
      GOTO_INITIALIZING: {
        target: 'initializing',
      },
      SET_IS_MULPAY_ORDER_ENABLED: {
        actions: ['assignDataIsMulpayOrderEnabled'],
      },
    },
  },
  {
    guards: {
      canTransitFromInitializingToWaitingForVerifying,
      canTransitFromInitializingToMain,
      canTransitFromMainToGettingStripeCreditCardTokenOnMain,
      canTransitFromMainToCompleteOnMain,
      canTransitFromMainToSubscriptionSubmittingOnConfirming,
      canTransitFromMainToTicketBookSubmittingOnConfirming,
      canTransitFromMainToCompleteWithMulpayCreditCardToken,
    },
    services: {
      getStripeCreditCardToken,
      submitSubscription,
      submitTicketBook,
    },
    actions: {
      assignDataMerchantPublicId: assign((context, event) => {
        if (event.type !== 'SET_MERCHANT_PUBLIC_ID') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            merchantPublicId: event.payload.merchantPublicId,
          },
        };
      }),
      assignDataProductPublicId: assign((context, event) => {
        if (event.type !== 'SET_PRODUCT_PUBLIC_ID') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            productPublicId: event.payload.productPublicId,
          },
        };
      }),
      assignDataProductDetail: assign((context, event) => {
        if (event.type !== 'SET_PRODUCT_DETAIL') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            productDetail: event.payload.productDetail,
          },
        };
      }),
      assignDataIsRequireVerifying: assign((context, event) => {
        if (event.type !== 'SET_IS_REQUIRE_VERIFYING') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            isRequireVerifying: true,
          },
        };
      }),
      assignDataIsJCBEnabled: assign((context, event) => {
        if (event.type !== 'SET_IS_JCB_ENABLED') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            isJCBEnabled: event.payload.isJCBEnabled,
          },
        };
      }),
      assignDataResourceSchemeType: assign((context, event) => {
        if (event.type !== 'SET_RESOURCE_SCHEME_TYPE') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            resourceSchemeType: event.payload.resourceSchemeType,
          },
        };
      }),
      assignDataStripeCreditCardTokenForGetStripeCreditCardToken: assign(
        (context, event) => {
          return {
            ...context,
            data: {
              ...context.data,
              stripeError: undefined,
              stripeConfirmationTokenId:
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (event as any).data,
            },
          };
        },
      ),
      assignDataStripeErrorForGetStripeCreditCardToken: assign(
        (context, event) => {
          return {
            ...context,
            data: {
              ...context.data,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              stripeError: (event as any).data,
            },
          };
        },
      ),
      assignSubscriptionSubmitResultForSubmit: assign((context, event) => {
        if (event.type !== 'done.invoke.subscriptionSubmitting') {
          return context;
        }

        if (context.data.resourceSchemeType === 'event_scheme') {
          // M3新予約フロー計測用
          eventTypeProductPurchaseEvent({
            type: 'subscription',
            purchasedPublicId:
              event.data.customer_subscription_public_id.toString(),
            customerPublicId: event.data.customer_public_id.toString(),
          });
        }
        if (context.data.resourceSchemeType === 'course_scheme') {
          // M1M4新予約フロー計測用
          courseTypeProductPurchaseEvent({
            type: 'subscription',
            purchasedPublicId:
              event.data.customer_subscription_public_id.toString(),
            customerPublicId: event.data.customer_public_id.toString(),
          });
        }

        purchaseEvent({
          step: 'completion',
          productPublicId: context.data.productPublicId?.toString() ?? '',
          customerPublicId: event.data.customer_public_id.toString(),
        });

        return {
          ...context,
          data: {
            ...context.data,
            customerSubscriptionSubmitResult: event.data,
          },
        };
      }),
      assignTicketBookSubmitResultForSubmit: assign((context, event) => {
        if (event.type !== 'done.invoke.ticketBookSubmitting') {
          return context;
        }

        if (context.data.resourceSchemeType === 'event_scheme') {
          // M3新予約フロー計測用
          eventTypeProductPurchaseEvent({
            type: 'ticket_book',
            purchasedPublicId: event.data.ticket_book_public_id.toString(),
            customerPublicId: event.data.customer_public_id.toString(),
          });
        }
        if (context.data.resourceSchemeType === 'course_scheme') {
          // M1M4新予約フロー計測用
          courseTypeProductPurchaseEvent({
            type: 'ticket_book',
            purchasedPublicId: event.data.ticket_book_public_id.toString(),
            customerPublicId: event.data.customer_public_id.toString(),
          });
        }

        purchaseEvent({
          step: 'completion',
          productPublicId: context.data.productPublicId?.toString() ?? '',
          customerPublicId: event.data.customer_public_id.toString(),
        });

        return {
          ...context,
          data: {
            ...context.data,
            ticketBookSubmitResult: event.data,
          },
        };
      }),
      assignSubmitErrorForSubmit: assign((context, event) => {
        return {
          ...context,
          data: {
            ...context.data,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            submitError: (event as any).data,
          },
        };
      }),
      assignInputCustomerInfo: assign((context, event) => {
        if (event.type !== 'INPUT_CUSTOMER_INFO') {
          return context;
        }

        return {
          ...context,
          input: {
            ...context.input,
            customerInfo: event.payload.customerInfo,
          },
        };
      }),
      assignInputPaymentMethod: assign((context, event) => {
        if (event.type !== 'INPUT_PAYMENT_METHOD') {
          return context;
        }

        return {
          ...context,
          input: {
            ...context.input,
            paymentMethod: event.payload.paymentMethod,
          },
        };
      }),
      unassignData: assign((context, event) => {
        if (event.type !== 'RESET_DATA') {
          return context;
        }

        return {
          ...context,
          data: {},
        };
      }),
      unassignInput: assign((context, event) => {
        if (event.type !== 'RESET_INPUT') {
          return context;
        }

        return {
          ...context,
          input: {},
        };
      }),
      assignDataStripePaymentElements: assign((context, event) => {
        if (event.type !== 'SET_STRIPE_PAYMENT_ELEMENTS') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            stripeElements: event.payload.stripeElements,
          },
        };
      }),
      resetDataStripePaymentElements: assign((context, event) => {
        if (event.type !== 'RESET_STRIPE_PAYMENT_ELEMENTS') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            stripeElements: undefined,
          },
        };
      }),
      assignDataStripeInstance: assign((context, event) => {
        if (event.type !== 'SET_STRIPE_INSTANCE') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            stripeV3Instance: event.payload.stripeV3Instance,
          },
        };
      }),
      assignMulpayPopupWindow: assign((context, event) => {
        if (event.type !== 'SET_MULPAY_POPUP_WINDOW') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            mulpayPopupWindow: event.payload.popupWindow,
          },
        };
      }),
      assignDataMulPayToken: assign((context, event) => {
        if (event.type !== 'SET_MULPAY_MP_TOKEN') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            mulpayCardData: {
              type: 'new',
              mulpayMpToken: event.payload.mulpayMpToken,
              mulpayError: undefined,
            },
          },
        };
      }),
      assignDataMulPayCardData: assign((context, event) => {
        if (event.type !== 'SET_MULPAY_CARD_DATA') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            mulpayCardData: event.payload.mulpayCardData,
          },
        };
      }),
      resetDataMulPayInput: assign((context, event) => {
        if (event.type !== 'RESET_MULPAY_INPUT') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            mulpayCardData: undefined,
          },
        };
      }),
      assignDataIsMulpayOrderEnabled: assign((context, event) => {
        if (event.type !== 'SET_IS_MULPAY_ORDER_ENABLED') {
          return context;
        }

        return {
          ...context,
          data: {
            ...context.data,
            isMulpayOrderEnabled: event.payload.isMulpayOrderEnabled,
          },
        };
      }),
    },
  },
);
